import moment from 'moment-timezone';
import FileSaver from 'file-saver';
const json2csv = require('json2csv');

const {
    INVESTIGATION_ENTITY_TYPES,
    RISK_LEVELS,
    AUTHENTICATION_EXPORT_FIELDS,
    AUTH_TYPES_TO_SHOW
} = require('@silverfort-nodelibs/common/utils/consts');
const { getFormattedLog, formatDate } = require('@silverfort-nodelibs/common/utils/common');
const { getRiskIndicatorByRiskRule } = require('@silverfort-nodelibs/common/utils/insight');

const { uppercaseFirstLetter, unixDateToText } = require('../utils');

const entityDataExport = (entityData, entityType) => {
    const entityDetails = entityData.details;
    entityDetails.tokensCount = entityData.tokensCount;
    entityDetails.rules = entityData.rules;
    entityDetails.riskLevel = entityData.RiskLevel;
    const entityGroups = entityData.groups || [];
    let data = {
        'Display Name': `${entityDetails.display_name}${entityDetails.is_deleted ? ' (deleted)' : ''}`,
        Groups: entityGroups.map((group) => group.name)
    };
    if (entityType.id === INVESTIGATION_ENTITY_TYPES.USER.id) {
        const mfaEnrollment = entityDetails.tokensCount
            ? `Yes, has ${entityDetails.tokensCount} MFA ${entityDetails.tokensCount === 1 ? 'token' : 'tokens'}`
            : 'No';

        data = {
            ...data,
            'User Principal Name': entityDetails.identifier,
            Domain: entityDetails.domain_name,
            'MFA Enrollment': mfaEnrollment,
            'SAM Account': entityDetails.user_name,
            Email: entityDetails.email
        };
    } else if (entityType.id === INVESTIGATION_ENTITY_TYPES.APPLICATION.id) {
        let updateDate = 'N/A';
        if (entityDetails.update_date) {
            const updateDateTime = moment.utc(entityDetails.update_date).local();
            updateDate = `${updateDateTime.format('L')}, ${updateDateTime.format('LTS')}`;
        }

        data = {
            ...data,
            'Tenant Id': entityDetails.tenant_id,
            'App Id': entityDetails.identifier,
            Created: updateDate
        };
    } else if (entityType.id === INVESTIGATION_ENTITY_TYPES.OKTA.id) {
        let updateDate = 'N/A';
        if (entityDetails.update_date) {
            const updateDateTime = moment.utc(entityDetails.update_date).local();
            updateDate = `${updateDateTime.format('L')}, ${updateDateTime.format('LTS')}`;
        }
        const status = entityDetails.status ? uppercaseFirstLetter(entityDetails.status.toLowerCase()) : 'N/A';

        data = {
            ...data,
            'App Id': entityDetails.identifier,
            Updated: updateDate,
            Status: status
        };
    } else {
        const operatingSystem = entityDetails.operating_system ? entityDetails.operating_system : 'n/a';
        const domainName = entityDetails.domain_name ? entityDetails.domain_name : 'n/a';
        const services = entityDetails.services ? entityDetails.services.length : 'n/a';
        const rules =
            entityDetails.rules && entityDetails.rules.length
                ? `Yes, ${entityDetails.rules.length} active ${
                      entityDetails.rules.length === 1 ? 'policy' : 'policies'
                  }`
                : 'No';

        data = {
            ...data,
            'Operating System': operatingSystem,
            Domain: domainName,
            Services: services,
            'Active Policy': rules,
            'Last logon': unixDateToText(entityDetails.last_logon_timestamp)
        };
    }
    const entityDetailsCsv = json2csv.parse(data);
    const blob = new Blob([entityDetailsCsv], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, 'entityDetails.csv');
};

function getAuthTypeUsageFromProtocolUsage(usageByProtocol, isSaas) {
    const usedAuthTypesUsage = {};
    const foundProtocols = [];
    const authTypeToHide = isSaas ? [AUTH_TYPES_TO_SHOW.RADIUS, AUTH_TYPES_TO_SHOW.PING_ADAPTER] : [];
    const authTypesToShow = isSaas
        ? Object.keys(AUTH_TYPES_TO_SHOW).filter((authType) => !authTypeToHide.includes(AUTH_TYPES_TO_SHOW[authType]))
        : Object.keys(AUTH_TYPES_TO_SHOW);

    for (const protocolName of authTypesToShow) {
        usedAuthTypesUsage[protocolName] = 0;
        if (AUTH_TYPES_TO_SHOW[protocolName].protocols.length > 0) {
            for (const protocol of AUTH_TYPES_TO_SHOW[protocolName].protocols) {
                usedAuthTypesUsage[protocolName] += usageByProtocol[protocol] || 0;
            }
        } else {
            usedAuthTypesUsage[protocolName] = usageByProtocol[protocolName] || 0;
        }

        if (usedAuthTypesUsage[protocolName] > 0) {
            foundProtocols.push(protocolName);
        }
    }
    return { usedAuthTypesUsage, foundProtocols };
}

const entityProtocolUsageExport = (data) => {
    const fields = [
        {
            label: 'Protocol Type',
            value: 'protocol_type'
        },
        {
            label: 'Amount',
            value: 'count'
        },
        {
            label: '%',
            value: 'percents'
        }
    ];

    let authSum = 0;
    const usageByProtocol = {};
    data.forEach((protocol) => {
        authSum += protocol.count;
        usageByProtocol[protocol.protocol_type.toUpperCase()] = protocol.count;
    });
    let parseData = [];
    if (authSum) {
        const usedAuthTypesUsage = getAuthTypeUsageFromProtocolUsage(usageByProtocol).usedAuthTypesUsage;

        parseData = Object.keys(usedAuthTypesUsage)
            .filter((authType) => usedAuthTypesUsage[authType] > 0)
            .map((authType) => {
                const percents = ((usedAuthTypesUsage[authType] / authSum) * 100).toFixed(1);
                return {
                    protocol_type: AUTH_TYPES_TO_SHOW[authType].label,
                    count: usedAuthTypesUsage[authType],
                    percents: `${percents}%`
                };
            });
    }

    const csv = json2csv.parse(parseData, { fields: fields });
    const blob = new Blob([csv], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, 'entityProtocolUsage.csv');
};

const entityAuthenticationOverTimeExport = (data) => {
    const formatData = data.map((hourlyAuths) => {
        const timestamp = moment.unix(hourlyAuths.timestamp).local().format('L HH:00');
        return {
            ...hourlyAuths,
            timestamp: timestamp
        };
    });
    const csv = json2csv.parse(formatData, {
        fields: formatData.length ? null : [{ lable: 'timestemp', value: 'timestemp' }]
    });
    const blob = new Blob([csv], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, 'entityAuthenticationOverTime.csv');
};

const entityActivitiesExport = (activities, rulesNames, logsDisplayAccountType, groupPoliciesNames) => {
    const fields = [
        { label: 'Time', value: 'time_stamp' },
        { label: 'Activity Type', value: 'activityType' },
        { lable: 'Risk Indicator Type', value: 'riskIndicatorType' },
        { label: 'Risk Indicator Action', value: 'riskIndicatorAction' },
        { label: 'Entity Risk Level', value: 'EntityRiskLevel' },
        { label: 'Entity Old Risk Level', value: 'EntityOldRiskLevel' },
        ...AUTHENTICATION_EXPORT_FIELDS.filter((field) => field.value !== 'time_stamp')
    ];
    const parseActivites = activities.map((activity) => {
        let parseActivity = {};
        if (activity.risk_indicator_type) {
            parseActivity.activityType = 'Risk Indicator Change';
            const riskIndicator = getRiskIndicatorByRiskRule(activity.risk_indicator_type);
            if (riskIndicator) {
                parseActivity.riskIndicatorType = riskIndicator.entityTitle;
            } else if (activity.description) {
                parseActivity.riskIndicatorType = activity.description;
            } else {
                parseActivity.riskIndicatorType = activity.risk_indicator_type;
            }
            parseActivity.riskIndicatorAction = activity.is_deleted ? 'Deleted' : 'Added';
            parseActivity.time_stamp = formatDate(activity.time_stamp);
        } else if (activity.old_risk_level) {
            parseActivity.activityType = 'Risk Level Change';
            parseActivity.time_stamp = formatDate(activity.time_stamp);
            parseActivity.EntityRiskLevel = Object.values(RISK_LEVELS)
                .find((risk) => risk.value === activity.risk_level)
                .label.toUpperCase();
            parseActivity.EntityOldRiskLevel = Object.values(RISK_LEVELS)
                .find((risk) => risk.value === activity.old_risk_level)
                .label.toUpperCase();
        } else {
            parseActivity.activityType = 'Authentication';
            parseActivity = {
                activityType: 'Authentication',
                ...getFormattedLog(
                    activity,
                    rulesNames,
                    logsDisplayAccountType,
                    undefined,
                    true,
                    false,
                    groupPoliciesNames
                )
            };
        }

        return parseActivity;
    });
    const csv = json2csv.parse(parseActivites, { fields: fields });
    const blob = new Blob([csv], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, 'entityActivities.csv');
};

export {
    entityDataExport,
    entityProtocolUsageExport,
    entityAuthenticationOverTimeExport,
    entityActivitiesExport,
    getAuthTypeUsageFromProtocolUsage
};
